import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Send, Bug, Lightbulb, HelpCircle, MessageSquare, Loader2, User, Mail } from 'lucide-react';
import MainNav from '../components/MainNav';

const feedbackTypes = [
  { id: 'bug', label: 'Bug Report', icon: Bug, description: 'Something isn\'t working correctly' },
  { id: 'suggestion', label: 'Suggestion', icon: Lightbulb, description: 'Ideas for improvement' },
  { id: 'question', label: 'Question', icon: HelpCircle, description: 'Need help with something' },
  { id: 'other', label: 'Other', icon: MessageSquare, description: 'General feedback' }
];

export default function Feedback() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    feedback_type: '',
    subject: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.feedback_type) {
      toast.error('Please select a feedback type');
      return;
    }
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await apiFetch('/feedback', {
        method: 'POST',
        body: JSON.stringify({
          feedback_type: formData.feedback_type,
          subject: formData.subject,
          description: formData.description
        })
      });
      
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <MainNav />
        <main className="pt-24 pb-12 px-4 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="glass-card p-12">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <Send className="h-8 w-8 text-green-500" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Thank You!</h1>
              <p className="text-zinc-400 mb-8">
                Your feedback has been submitted successfully. We appreciate you taking the time to help us improve.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                Back to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNav />
      
      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Send Feedback</h1>
              <p className="text-zinc-400">Help us improve by sharing your thoughts</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Feedback Type Selection */}
            <div className="glass-card p-6">
              <Label className="text-white mb-4 block">What type of feedback is this?</Label>
              <div className="grid grid-cols-2 gap-3">
                {feedbackTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, feedback_type: type.id })}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      formData.feedback_type === type.id
                        ? 'border-primary bg-primary/10'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                    data-testid={`feedback-type-${type.id}`}
                  >
                    <type.icon className={`h-5 w-5 mb-2 ${
                      formData.feedback_type === type.id ? 'text-primary' : 'text-zinc-400'
                    }`} />
                    <div className={`font-medium ${
                      formData.feedback_type === type.id ? 'text-primary' : 'text-white'
                    }`}>
                      {type.label}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{type.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* User Info (Read-only from account) */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Your Information</h3>
              <p className="text-zinc-500 text-sm mb-4">Automatically filled from your account</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Name
                  </Label>
                  <div className="mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white">
                    {user?.name || 'Unknown'}
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </Label>
                  <div className="mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white">
                    {user?.email || 'Unknown'}
                  </div>
                </div>
              </div>
            </div>

            {/* Feedback Details */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Feedback Details</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="subject" className="text-zinc-300">Subject</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief summary of your feedback"
                    className="input-dark mt-1"
                    required
                    data-testid="feedback-subject"
                  />
                </div>
                <div>
                  <Label htmlFor="description" className="text-zinc-300">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please provide as much detail as possible..."
                    className="input-dark mt-1 min-h-[150px]"
                    required
                    data-testid="feedback-description"
                  />
                  {formData.feedback_type === 'bug' && (
                    <p className="text-xs text-zinc-500 mt-2">
                      Tip: Include what you were trying to do, what happened, and what you expected to happen.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full py-6"
              data-testid="feedback-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
          </form>

          {/* Alternative Contact */}
          <div className="text-center mt-8 text-zinc-500 text-sm">
            <p>
              You can also reach us at{' '}
              <a href="mailto:support@wrappedinmusic.com" className="text-primary hover:underline">
                support@wrappedinmusic.com
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
